import React from "react";
import { PartnerResource } from "../../../../api/partners";
import { Host, fromPartnerHost, hostLabel } from "../../../../model/host";
import { useBoundStore } from "../../../../store/store";

export interface HostOption {
  host: Host;
  /** What the picker shows: the name, suffixed with the partner for its staff. */
  label: string;
}

export interface HostOptions {
  options: HostOption[];
  /** Every option's host by id — what a picked id maps back to. */
  byId: Record<string, Host>;
  /** Why the partner's staff are missing from the list, when they are. */
  partnerError: string | null;
}

interface PartnerHosts {
  partnerId: string;
  hosts: Host[];
}

/**
 * The hosts a meeting's picker may offer: the Hosts group (the store's
 * `hostsById`), plus the partner's staff when the meeting is scoped to a
 * partner (ENG-1940). Whatever is already selected but in neither set stays
 * selectable as it is — a placeholder "Unknown host (<id>)" — so a re-save
 * sends the id back unchanged rather than dropping it.
 */
export const useHostOptions = (
  partnerId: string | undefined,
  selected: (Host | undefined)[],
): HostOptions => {
  const hostsById = useBoundStore((state) => state.hostsById);
  const scopedTo = partnerId?.trim() || undefined;
  const [partnerHosts, setPartnerHosts] = React.useState<PartnerHosts | null>(
    null,
  );
  const [partnerError, setPartnerError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!scopedTo) {
      setPartnerHosts(null);
      setPartnerError(null);
      return;
    }
    let cancelled = false;
    // An earlier scope's failure must not stay on screen while this one loads.
    setPartnerError(null);
    Promise.all([
      PartnerResource.listHosts(scopedTo),
      // The partner's name labels its staff; the picker still works without it.
      PartnerResource.getListCached().catch(() => []),
    ])
      .then(([hosts, partners]) => {
        if (cancelled) return;
        const partnerName =
          partners.find((p) => p.id === scopedTo)?.displayName ?? scopedTo;
        setPartnerHosts({
          partnerId: scopedTo,
          hosts: hosts.map((h) => fromPartnerHost(h, partnerName)),
        });
        setPartnerError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setPartnerHosts(null);
        setPartnerError("Could not load the partner's staff hosts.");
      });
    return () => {
      cancelled = true;
    };
  }, [scopedTo]);

  // Hosts from an earlier scope must not linger while the new one loads.
  const staff = partnerHosts?.partnerId === scopedTo ? partnerHosts : null;
  // `selected` is a fresh array each render; only its hosts matter.
  const selectedKey = selected.map((h) => h?.id ?? "").join("\u0000");

  return React.useMemo(() => {
    const byId: Record<string, Host> = {};
    const options: HostOption[] = [];
    const add = (host: Host) => {
      if (byId[host.id]) return;
      byId[host.id] = host;
      options.push({ host, label: hostLabel(host) });
    };
    // A staff member who is also in the Hosts group is listed once, as staff.
    for (const host of staff?.hosts ?? []) add(host);
    for (const host of Object.values(hostsById)) add(host);
    for (const host of selected) {
      if (host) add(host);
    }
    return { options, byId, partnerError };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostsById, staff, partnerError, selectedKey]);
};
